import { createSlice } from "@reduxjs/toolkit";
import { getSocket } from "../../utils/socket";
import * as _ from "lodash";

const initialState = {
  message: null,
  auth: {},
  error: "",
  isConnected: true,
  sessionChange: null,
  accountId: null,
  requesterStatusChange: null,
  staffChangeRequesterStatus: null,
  sessionChangeToRequester: null,
  requesterTrackingChange: null,
  sessionConvertTicket: null,
  staffStatus: null,
  isSound: true,
  staffChangeGroup: null,
  typingBySession: {},
};

const socketSlice = createSlice({
  name: "socketManage",
  initialState,
  reducers: {
    clearMessage: (state) => {
      state.message = null;
    },
    clearReceiveRequesterStatusChange: (state) => {
      state.requesterStatusChange = null;
    },
    clearSessionChangeToRequester: (state) => {
      state.sessionChangeToRequester = null;
    },
    clearSessionChange: (state) => {
      state.sessionChange = null;
    },
    clearSessionSeen: (state) => {
      state.sessionSeen = null;
    },
    clearStaffChangeRequesterStatus: (state) => {
      state.staffChangeRequesterStatus = null;
    },
    clearSessionCovertTicket: (state) => {
      state.sessionConvertTicket = null;
    },
    clearStaffStatus: (state) => {
      state.staffStatus = null;
    },
    clearAuth: (state) => {
      state.auth = {};
    },
    receiveMessage: (state, action) => {
      const message = action.payload;
      state.message = message;
    },
    receiveAuth: (state, action) => {
      state.auth = action.payload;
    },
    receiveSessionsChange: (state, action) => {
      const session = action.payload;
      state.sessionChange = session;
    },
    receiveSessionsSeen: (state, action) => {
      const session = action.payload;
      state.sessionSeen = session;
    },
    receiveRequesterStatusChange: (state, action) => {
      const requesterId = action.payload;
      state.requesterStatusChange = { requesterId };
    },
    receiveRequesterTracking: (state, action) => {
      const requesterId = action.payload;
      state.requesterTrackingChange = { requesterId };
    },
    receiveStaffChangeGroup: (state, action) => {
      const staffId = action.payload;
      state.staffChangeGroup = { staffId };
    },
    receiveSessionChangeToRequester: (state, action) => {
      const session = action.payload;

      state.sessionChangeToRequester = session;
    },
    receiveStaffChangeRequesterStatus: (state, action) => {
      const requesterId = action.payload;
      state.staffChangeRequesterStatus = { requesterId };
    },
    receiveSessionCovertTicket: (state, action) => {
      const session = _.get(action.payload, "session", null);
      state.sessionConvertTicket = session;
    },
    receiveStaffStatus: (state, action) => {
      const staffId = action.payload;
      state.staffStatus = { staffId };
    },
    receiveError: (state, action) => {
      state.error = action.payload;
    },
    resetError: (state) => {
      state.error = "";
    },
    connectionEstablished: (state) => {
      state.isConnected = true;
    },
    connectionLost: (state) => {
      state.isConnected = false;
    },
    setAccountId: (state, action) => {
      state.accountId = action.payload;
    },
    setIsSound(state, action) {
      state.isSound = action.payload;
    },
    receiveTypingUpdate: (state, action) => {
      const payload = action.payload;
      const sessionId = _.get(payload, "sessionId");
      const userId = _.get(payload, "userId");
      const userName = _.get(payload, "userName");
      const event = _.get(payload, "event");

      if (!sessionId || !userId) {
        return;
      }

      if (!_.has(state.typingBySession, sessionId)) {
        state.typingBySession[sessionId] = {};
      }

      if (event === "typing_start") {
        state.typingBySession[sessionId][userId] = { userName };
      } else if (event === "typing_stop") {
        state.typingBySession[sessionId] = _.omit(
          state.typingBySession[sessionId],
          userId,
        );
      }
    },
  },
});

export const initializeSocket = (socketToken) => (dispatch) => {
  const socket = getSocket(socketToken);

  socket.on("connect", () => {
    dispatch(socketActions.connectionEstablished());
  });

  socket.on("disconnect", () => {
    dispatch(socketActions.connectionLost());
  });

  socket.on("server_message", (message) => {
    dispatch(socketActions.receiveMessage(message));
  });

  socket.on("sessions_change", (message) => {
    dispatch(socketActions.receiveSessionsChange(message));
  });

  socket.on("sessions_seen", (message) => {
    dispatch(socketActions.receiveSessionsSeen(message));
  });

  socket.on("requester_status", (message) => {
    dispatch(socketActions.receiveRequesterStatusChange(message));
  });

  socket.on("requester_tracking", (message) => {
    dispatch(socketActions.receiveRequesterTracking(message));
  });

  socket.on("staff_change_group", (message) => {
    dispatch(socketActions.receiveStaffChangeGroup(message));
  });

  socket.on("session_change_to_requester", (message) => {
    dispatch(socketActions.receiveSessionChangeToRequester(message));
  });

  socket.on("staff_change_requester_status", (message) => {
    dispatch(socketActions.receiveStaffChangeRequesterStatus(message));
  });

  socket.on("sessions_convert_ticket", (message) => {
    dispatch(socketActions.receiveSessionCovertTicket(message));
  });

  socket.on("staff_status", (message) => {
    dispatch(socketActions.receiveStaffStatus(message));
  });

  socket.on("error", (message) => {
    dispatch(socketActions.receiveError(message));
  });

  socket.on("reconnect", () => {
    dispatch(socketActions.connectionEstablished());
  });

  socket.on("reconnect_failed", () => {
    dispatch(socketActions.connectionLost());
  });

  socket.on("authenticated", (auth) => {
    dispatch(socketActions.receiveAuth(auth));
  });

  socket.on("typing_update", (message) => {
    dispatch(socketActions.receiveTypingUpdate(message));
  });

  return () => {
    socket.off("connect");
    socket.off("disconnect");
    socket.off("receive_message");
    socket.off("error");
    socket.off("reconnect");
    socket.off("reconnect_failed");
    socket.off("authenticated");
    socket.off("typing_update");
  };
};

export const sendMessage = (payload) => () => {
  const socket = getSocket();

  if (!socket) {
    return;
  }

  socket.emit("client_message", payload);
};

export const sendTypingStart = (payload) => () => {
  const socket = getSocket();

  if (!socket) {
    return;
  }

  socket.emit("typing_message", { ...payload, event: "typing_start" });
};

export const sendTypingStop = (payload) => () => {
  const socket = getSocket();

  if (!socket) {
    return;
  }

  socket.emit("typing_message", { ...payload, event: "typing_stop" });
};

export const socketReducer = socketSlice.reducer;
export const socketActions = socketSlice.actions;
